import { WeddingWebsiteSiteDetails } from "../../resources/modelTypes";
import { selectors } from "../cypressSelectors";
import { SectionHeader } from "../shared-components/SectionHeader";
import { sharedSectionTypeStyles as styles } from "./section-types/sharedSectionTypeStyles";
import { css } from "@emotion/react";
import {
  Divider,
  Icon,
  IconButton,
  styleUtils,
  TextLink,
  theme,
} from "@minted/minted-components";
import React, { memo, useCallback } from "react";

const siteDetailsStyles = {
  emptySection: css`
    border: ${styleUtils.rem(1)} dashed ${theme.colors.gray300};
    border-radius: ${theme.spacing.x2};
    padding: ${theme.spacing.x4};

    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  emptySectionContent: css`
    ${theme.typeStyles.bodyExtraSmallStyles};
  `,
  emptySectionHeader: css`
    ${theme.typeStyles.titleExtraSmallStyles};
  `,
  visibilityIcon: css`
    margin-top: ${theme.spacing.base};
  `,
  visibilityText: css`
    display: flex;
    align-items: center;
    margin-right: auto;
    margin-left: ${theme.spacing.x2};
    gap: ${theme.spacing.base};
    ${theme.typeStyles.bodyExtraSmallStyles};
    color: ${theme.colors.textSecondary};
  `,
};

export enum SiteDetailsSections {
  YourNames = "your_names",
  WelcomeMessage = "welcome_message",
  TheBigDay = "the_big_day",
  EventNameAndDescription = "event_name_and_description",
}

export type CollapsedSiteDetailsProps = {
  editSiteDetailsSection: (detailsSection: SiteDetailsSections) => void;
  siteDetails: WeddingWebsiteSiteDetails;
  siteLayout: "l1" | "l2" | "l3" | "l4";
};

const CollapsedSiteDetails = ({
  editSiteDetailsSection,
  siteDetails,
  siteLayout,
}: CollapsedSiteDetailsProps) => {
  const startEditingNames = useCallback(() => {
    editSiteDetailsSection(SiteDetailsSections.YourNames);
  }, [editSiteDetailsSection]);
  const startEditingBigDay = useCallback(() => {
    editSiteDetailsSection(SiteDetailsSections.TheBigDay);
  }, [editSiteDetailsSection]);
  const startEditingWelcomeMessage = useCallback(() => {
    editSiteDetailsSection(SiteDetailsSections.WelcomeMessage);
  }, [editSiteDetailsSection]);

  return (
    <div>
      <div
        css={styles.wrapper}
        data-cy={`${selectors.sections_collapsedSection}_yourNames`}
      >
        <SectionHeader title="Your Names" onEdit={startEditingNames} />
        <Divider type="light" />
        <div css={styles.readOnlyDescriptionGridLayout}>
          <div css={styles.label}>Names</div>
          <div
            css={styles.plainTextValue}
            data-cy={`${selectors.sections_collapsedField}_names`}
          >
            {siteDetails.yourName || "Your Name"}{" "}
            {siteLayout === "l3" ? "~" : siteDetails.nameSeparator || "and"}{" "}
            {siteDetails.spouseName || "Spouse Name"}
          </div>
        </div>
      </div>
      <div
        css={styles.wrapper}
        data-cy={`${selectors.sections_collapsedSection}_weddingDate`}
      >
        <div css={styles.row}>
          <div
            css={styles.sectionTitle}
            data-cy={selectors.sections_collapsedPrimaryText}
          >
            Wedding Date
          </div>
          {siteDetails.weddingDate && (
            <div data-cy={selectors.sections_collapsedEditButton}>
              <TextLink
                onClick={startEditingBigDay}
                size="small"
                text="Edit"
                type="secondary"
              />
            </div>
          )}
        </div>
        <Divider type="light" />
        {siteDetails.weddingDate ? (
          <div css={styles.readOnlyDescriptionGridLayout}>
            <div css={styles.label}>Heading</div>
            <div
              css={styles.plainTextValue}
              data-cy={`${selectors.sections_collapsedField}_eventDateHeading`}
            >
              {siteDetails.eventDateHeading}
            </div>
            <div css={styles.label}>Date</div>
            <div
              css={styles.plainTextValue}
              data-cy={`${selectors.sections_collapsedField}_weddingDate`}
            >
              {siteDetails.weddingDate}
            </div>
            {siteDetails.isCountdownVisible && (
              <>
                <div css={styles.label}>Countdown</div>
                <div
                  css={styles.plainTextValue}
                  data-cy={`${selectors.sections_collapsedField}_countdownText`}
                >
                  {siteDetails.countdownText}
                </div>
              </>
            )}
          </div>
        ) : (
          <div css={siteDetailsStyles.emptySection}>
            <div>
              <div css={siteDetailsStyles.emptySectionHeader}>
                Add Your Wedding Date
              </div>
              <div css={siteDetailsStyles.emptySectionContent}>
                Set your date and an optional countdown to your celebration.
              </div>
            </div>
            <IconButton
              iconType={Icon.types.add}
              onClick={startEditingBigDay}
              size={IconButton.sizes.medium}
              type={IconButton.types.primary}
            />
          </div>
        )}
      </div>
      <div
        css={styles.wrapper}
        data-cy={`${selectors.sections_collapsedSection}_welcomeMessage`}
      >
        <div css={styles.row}>
          <div
            css={styles.sectionTitle}
            data-cy={selectors.sections_collapsedPrimaryText}
          >
            {siteDetails.welcomeHeading || "Welcome Message"}
          </div>
          {(siteDetails.welcomeHeading || siteDetails.welcomeText) && (
            <>
              {!siteDetails.isOurStoryVisible && (
                <div
                  css={siteDetailsStyles.visibilityText}
                  data-cy={`${selectors.sections_collapsedField}_visibility`}
                >
                  <div css={siteDetailsStyles.visibilityIcon}>
                    <Icon type={Icon.types.invisible} />
                  </div>
                  <div>Not Visible</div>
                </div>
              )}
              <div data-cy={selectors.sections_collapsedEditButton}>
                <TextLink
                  onClick={startEditingWelcomeMessage}
                  size="small"
                  text="Edit"
                  type="secondary"
                />
              </div>
            </>
          )}
        </div>
        <Divider type="light" />
        {siteDetails.welcomeHeading || siteDetails.welcomeText ? (
          <div css={styles.readOnlyDescriptionGridLayout}>
            <div css={styles.label}>Description</div>
            <div
              css={styles.plainTextValue}
              data-cy={`${selectors.sections_collapsedField}_richText`}
            >
              {siteDetails.welcomeText}
            </div>
          </div>
        ) : (
          <div css={siteDetailsStyles.emptySection}>
            <div>
              <div css={siteDetailsStyles.emptySectionHeader}>
                Add a Welcome Message
              </div>
              <div css={siteDetailsStyles.emptySectionContent}>
                Share your unique love story and set the tone for your special
                day.
              </div>
            </div>
            <IconButton
              iconType={Icon.types.add}
              onClick={startEditingWelcomeMessage}
              size={IconButton.sizes.medium}
              type={IconButton.types.primary}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const MemoizedCollapsedSiteDetails = memo(CollapsedSiteDetails);

export { MemoizedCollapsedSiteDetails as CollapsedSiteDetails };
